import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ClipboardList } from "lucide-react";
import SEO from "../components/SEO";
import { CALENDLY_URL, ALIGNMENT_CHECK_URL } from "../lib/site";

/* -------------------------------------------------------------------------
 *  Insight 001 — Leadership Doesn't Have a Data Problem.
 *  The flagship piece for the AlignED Insights library.
 *  Written under the Quiet Confidence Principle™. Teaches before it sells.
 * ------------------------------------------------------------------------- */

export default function InsightLeadershipData() {
  return (
    <>
      <SEO
        title="Leadership Doesn't Have a Data Problem | AlignED Insights"
        description="Educational leaders are surrounded by information. The real challenge is turning information into consistently better leadership decisions. Insight 001 from the AlignED library."
      />

      {/* Article Hero */}
      <section className="bg-[#FDFBF7] border-b border-[#0A192F]/10" data-testid="insight-hero">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-20 pb-12 lg:pt-24 lg:pb-16">
          <Link
            to="/insights"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#52796F] hover:text-[#0A192F] transition-colors"
            data-testid="insight-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Insights
          </Link>
          <div className="mt-8 text-xs uppercase tracking-[0.25em] text-[#38BDF8]">
            Insight 001 &middot; Leadership Clarity
          </div>
          <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] text-[#0A192F]">
            Leadership Doesn&apos;t Have a Data Problem.
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
            Educational leaders are surrounded by information. The real challenge is turning information into consistently better leadership decisions.
          </p>
        </div>
      </section>

      {/* Article Body */}
      <article className="bg-[#FDFBF7]" data-testid="insight-body">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 pb-24">
          <div className="prose-editorial text-[#1A1D20] leading-[1.75] text-lg space-y-7">

            <p>
              Educational leaders are surrounded by reports, dashboards, spreadsheets, and updates. School systems generate more information every year than any leadership team could reasonably absorb. And yet, most leadership meetings still end with the same question:
            </p>

            <p className="font-serif-display text-2xl sm:text-3xl text-[#0A192F] leading-snug border-l-2 border-[#38BDF8] pl-6 py-1">
              &ldquo;What should we actually focus on?&rdquo;
            </p>

            <p>
              That question does not appear because the team lacks information. It appears because information alone does not create clarity. Attention does. And clarity is what leadership actually needs.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              The reframe
            </h2>

            <p>
              Leadership does not primarily suffer from a lack of information. Leadership suffers when information never becomes clarity.
            </p>

            <p>
              A district can add another dashboard, another data pull, another intervention platform, and the fundamental leadership question does not become easier to answer. The volume of information rises. The clarity does not.
            </p>

            <p>
              Something else is happening.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              Why organizations become overwhelmed
            </h2>

            <p>
              In our work with educational leaders, four patterns come up again and again.
            </p>

            <p>
              <strong className="font-serif-display text-[#0A192F]">Competing priorities.</strong> Every initiative has a champion. Every champion has a metric. Every metric has a dashboard. Over time, the leadership team ends up with fifteen dashboards, thirty priorities, and no shared answer to which one this week actually deserves the leadership team&apos;s attention.
            </p>

            <p>
              <strong className="font-serif-display text-[#0A192F]">Disconnected information.</strong> Attendance lives in one system. Behavior lives in another. Enrollment lives in a third. Each is well-organized inside its own tool. None of them speak to each other in the shape a leader actually thinks in — which is not by system but by student, by classroom, by grade, by week.
            </p>

            <p>
              <strong className="font-serif-display text-[#0A192F]">Inconsistent follow-through.</strong> Even when the meeting reaches a decision, follow-through varies by school, by team, by week. Two weeks later, the team is often deciding the same question again — not because the answer changed, but because the answer never fully landed.
            </p>

            <p>
              <strong className="font-serif-display text-[#0A192F]">Limited visibility into execution.</strong> The information leaders have easiest access to is often about outcomes that have already occurred. The information they most need — is the plan working, is the work happening consistently, are the small course-corrections landing — is the hardest to see.
            </p>

            <p>
              None of this is a data problem. All of it is a clarity problem.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              What better leadership actually needs
            </h2>

            <p>
              Stronger leadership begins by helping leaders do five things well.
            </p>

            <p>
              First, <em>see reality more clearly</em> — not more comprehensively, more clearly. There is a difference. A superintendent does not need to see everything every day. She needs to see the few things that would change how the week goes if she saw them.
            </p>

            <p>
              Second, <em>recognize meaningful patterns</em>. A single referral is an incident. Three referrals in the same classroom in ten days is a pattern. Twenty referrals across the district in the same grade is a system. Great leadership is often about seeing the shape one level up from where the information first appears.
            </p>

            <p>
              Third, <em>choose deliberately</em>. The most common failure of leadership meetings is not that the wrong decisions get made — it is that no decisions get made. A dozen items get discussed. Two get chosen. Two get executed. The rest carry into the next week.
            </p>

            <p>
              Fourth, <em>execute more consistently</em>. Execution is the least glamorous of the five and by far the most decisive. Almost every school improvement effort we have observed fails not because of the plan but because of the consistency of the follow-through in weeks four, five, and six.
            </p>

            <p>
              Fifth, <em>learn continuously</em>. Great organizations do not treat every year as a blank sheet. They capture what worked, help it spread, and leave behind the practices that did not. Most schools have the raw material for organizational learning. Very few have a system for it.
            </p>

            <h2 className="font-serif-display text-3xl text-[#0A192F] leading-tight pt-8">
              A different starting point
            </h2>

            <p>
              The next generation of educational leadership will not be defined by which district collected the most data. It will be defined by which district turned information into consistently better leadership decisions.
            </p>

            <p>
              That is a different question than the one most systems are currently built to answer. And it changes the starting point for what a leadership system should be designed to do.
            </p>

            <p className="font-serif-display text-2xl text-[#0A192F] leading-snug pt-4">
              Schools do not need more information.
            </p>
            <p className="font-serif-display text-2xl text-[#0A192F] leading-snug -mt-4">
              They need more clarity.
            </p>

            <p className="pt-2">
              Because better leadership creates better systems. Better systems create more consistent execution. And more consistent execution creates better student experiences.
            </p>

            <p>
              That is the shift.
            </p>
          </div>

          {/* Contextual Invitation — connects directly to the article's argument */}
          <div className="mt-20 pt-10 border-t border-[#0A192F]/10">
            <p className="text-base text-[#4A5568] leading-relaxed">
              If this perspective resonates with your leadership team, the next step is not more information.
            </p>
            <p className="mt-2 font-serif-display text-xl sm:text-2xl text-[#0A192F] leading-snug">
              It is greater clarity.
            </p>
            <p className="mt-6 text-base text-[#4A5568] leading-relaxed">
              Take the Alignment Check&trade; to reflect on where visibility, ownership, and follow-through may be drifting in your organization.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={ALIGNMENT_CHECK_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 bg-[#0A192F] text-[#FDFBF7] px-8 py-3.5 text-sm tracking-wide hover:bg-[#112240] transition-colors border border-[#0A192F]"
                data-testid="insight-cta-ac"
              >
                <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Take the Alignment Check&trade;
              </a>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex justify-center items-center gap-2 text-[#0A192F] text-sm tracking-wide border-b border-[#0A192F] pb-1 self-start sm:self-center hover:text-[#52796F] hover:border-[#52796F] transition-colors"
                data-testid="insight-cta-walkthrough"
              >
                Schedule a Walkthrough <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="mt-16 flex justify-center">
            <Link
              to="/insights"
              className="inline-flex items-center gap-2 text-sm text-[#52796F] hover:text-[#0A192F] transition-colors border-b border-[#52796F]/40 hover:border-[#0A192F] pb-1"
              data-testid="insight-return-link"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Return to Insights
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
